import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import Writing from "./pages/Writing";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import FAQ from "./pages/FAQ";
import NotFound from "./pages/NotFound";

// Lazy-loaded admin pages for code splitting
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminLetters = lazy(() => import("./pages/admin/AdminLetters"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminResetPassword = lazy(() => import("./pages/admin/AdminResetPassword"));

const queryClient = new QueryClient();

// Loading fallback for lazy-loaded components
const AdminLoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
      <p className="text-muted-foreground font-sans text-sm">Loading...</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/writing" element={<Writing />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/faq" element={<FAQ />} />
          
          {/* Admin Routes - Lazy loaded for code splitting */}
          <Route path="/admin" element={
            <Suspense fallback={<AdminLoadingFallback />}>
              <AdminLayout />
            </Suspense>
          }>
            <Route index element={
              <Suspense fallback={null}>
                <AdminDashboard />
              </Suspense>
            } />
            <Route path="orders" element={
              <Suspense fallback={null}>
                <AdminOrders />
              </Suspense>
            } />
            <Route path="letters" element={
              <Suspense fallback={null}>
                <AdminLetters />
              </Suspense>
            } />
            <Route path="users" element={
              <Suspense fallback={null}>
                <AdminUsers />
              </Suspense>
            } />
            <Route path="settings" element={
              <Suspense fallback={null}>
                <AdminSettings />
              </Suspense>
            } />
          </Route>
          <Route path="/admin/reset-password" element={
            <Suspense fallback={<AdminLoadingFallback />}>
              <AdminResetPassword />
            </Suspense>
          } />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
